import React, { memo, useState, useEffect } from 'react';
import moment from 'moment';
import { Formik } from 'formik';
import { has, isEmpty, sortBy } from 'lodash';
import * as Yup from 'yup';
import Dialog from 'components/ui/Dialog';
import { FormSectionTitle } from 'styles/form';
import Form from 'components/ui/Form';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { exportScheduler } from 'api/scheduler';
import { useSnackBar } from 'context/SnackBarContext';

const SchedulerExportModal = ({
  scheduler,
  schedulerId,
  exportModalOpen,
  setExportModalOpen,
}) => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();
  const handleModalClose = () => {
    setExportModalOpen(false);
  };

  const handleExportPdf = async () => {
    const start = moment(startDate).format('MM/DD/YYYY');
    const end = moment(endDate).format('MM/DD/YYYY');
    try {
      const data = await exportScheduler(schedulerId, start, end);
      const blob = new Blob([data.data], {
        type: 'application/octet-stream',
        encoding: 'UTF-8',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `schedule${schedulerId}_${start}_${end}.csv`;
      link.click();
    } catch (error) {
      if (error.response.data.message) {
        setSeverity('error');
        setMessage(error.response.data.message);
        setOpenSnackBar(true);
        setExportModalOpen(false);
      }
    }
  };
  const minStartDate = moment(scheduler.min_start_date).format('MM/DD/YYYY');
  const maxEndDate = moment(scheduler.max_end_date).format('MM/DD/YYYY');
  React.useEffect(() => {
    if (scheduler.min_start_date !== null) {
      return setStartDate(minStartDate);
    }
  }, [scheduler.min_start_date]);
  React.useEffect(() => {
    if (scheduler.min_start_date !== null) {
      return setEndDate(minStartDate);
    }
  }, [scheduler.min_start_date]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };
  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  return (
    <Formik
      initialValues={{
        startDate: new Date(),
        endDate: new Date(),
      }}
      validationSchema={Yup.object().shape({
        startDate: Yup.date().required('field is required'),
        endDate: Yup.date().required('field is required'),
      })}
      enableReinitialize
      onSubmit={handleExportPdf}
    >
      {({ values: { date }, errors, handleChange, submitForm, touched }) => (
        <Dialog
          open={exportModalOpen}
          handleClose={handleModalClose}
          disableTopPadding
          maxWidth="xs"
          typeText="Appointment"
          submitForm={submitForm}
          disableDelete
          buttonText="Export Scheduler"
        >
          <FormSectionTitle>Export Scheduler</FormSectionTitle>
          <Form fullWidth onSubmit={submitForm}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <div className="row">
                <div className="col-md-6">
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    inputVariant="outlined"
                    format="MM/dd/yyyy"
                    margin="normal"
                    label="Start Date"
                    value={startDate}
                    minDate={minStartDate}
                    maxDate={maxEndDate}
                    onChange={handleStartDateChange}
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    required
                    name="startDate"
                    errors={touched.startDate && has(errors, 'startDate')}
                    touched={touched}
                    helperText={touched.startDate && errors.startDate}
                  />
                </div>
                <div className="col-md-6">
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    inputVariant="outlined"
                    format="MM/dd/yyyy"
                    margin="normal"
                    label="End Date"
                    value={endDate}
                    minDate={minStartDate}
                    maxDate={maxEndDate}
                    onChange={handleEndDateChange}
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    required
                    name="endDate"
                  />
                </div>
              </div>
            </MuiPickersUtilsProvider>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};

export default memo(SchedulerExportModal);
